import 'lib-js-password/lib/passwordValidator';
import { Component } from './Component';
import { SessionService } from '../services/SessionService';
import { IdentityError } from '../errors/IdentityError';
import { PasswordValidator } from '../validators/PasswordValidator';
import { PasswordVisibility } from './PasswordVisibility';
import { Validator } from '../validators/Validator';
import { ApiService } from '../services/ApiService';
import { InputEvent } from '../utils/InputEvent';

/**
 * Identity Component.
 */
export class Identity extends Component {
  /**
   * String of the dom element of the radio buttons.
   *
   * @type {string}
   */
  static civilityDOMSelector = 'input[name="civility"]';

  /**
   * String of the dom element of the last name id.
   *
   * @type {string}
   */
  static lastNameDOMSelector = '#lastName';

  /**
   * String of the dom element of the title of civility.
   *
   * @type {string}
   */
  static civilityTitleDOMSelector = '#titleCivility';

  /**
   * String of the dom element of the last name id.
   *
   * @type {string}
   */
  static firstNameDOMSelector = '#firstName';

  /**
   * String of the dom element of the civility label id.
   *
   * @type {string}
   */
  static civilityLabelDOMSelector = '#civilityLabel';

  /**
   * String of the dom element of the lastName label id.
   *
   * @type {string}
   */
  static lastNameLabelDOMSelector = '#lastNameLabel';

  /**
   * String of the dom element of the lastName label id.
   *
   * @type {string}
   */
  static firstNameLabelDOMSelector = '#firstNameLabel';

  /**
   * String of the dom element of the cgu id.
   *
   * @type {string}
   */
  static cguDOMSelector = '#cgu';

  /**
   * Cgu link css selector.
   *
   * @type {string}
   */
  static cguLinkDOMSelector = '#cguLink';

  /**
   * Gdpr link css selector.
   *
   * @type {string}
   */
  static gdprLinkDOMSelector = '#gdprLink';

  /**
   * String of the dom element of the email id.
   *
   * @type {string}
   */
  static emailDOMSelector = '#email';

  /**
   * String of the dom element of the email label.
   *
   * @type {string}
   */
  static emailLabelDOMSelector = '#emailLabel';

  /**
   * String of the dom element of the password id
   * password css selector.
   *
   * @type {string}
   */
  static passwordDOMSelector = '#password';

  /**
   * String of the dom element of the password id
   * password css selector.
   *
   * @type {string}
   */
  static passwordLabelDOMSelector = '#passwordLabel';

  /**
   * String of the dom element of the submit button.
   *
   * @type {string}
   */
  static btnSubmitDOMSelector = '#btnSubmit';

  /**
   * String of the dom element of the main form.
   *
   * @type {string}
   */
  static mainFormDOMSelector = '#eui-form-identity';

  /**
   * Password Validator conf that overrides default values & methods used by validator.
   *
   * @type {object} the conf object
   */
  static passwordValidatorConf = PasswordValidator.getConf();

  /**
   * Static password value.
   *
   * @type {string}
   */
  static passwordValue = '';

  /**
   * Constructor.
   *
   * @param {string} container - The selector of the container of the component.
   */
  constructor(container) {
    super(container);
    this.identityError = new IdentityError();
  }

  /**
   * Render template.
   *
   * @param {object} opts - The parameters to render the component.
   */
  render(opts) {
    if (opts.isBack || opts.fromCaptcha) {
      Component.updateTracking({ type_page: 'infos_perso' });
    }
    if (opts) {
      super.render(opts);
      this.attachListener(opts.isBack || false);
      // Tell the visually impaired that the page has changed
      super.announceNextPageLoad();
    }
  }

  /**
   * Back event treatment for civility.
   */
  back() {
    window.history.back();

    // Check if captcha is enable
    if (Eui.captchaConfig.enabled) {
      window.history.back();
    }
  }

  /**
   * Attach listener for the actual component.
   *
   * @param {boolean} isBack - Check if back button is pressed.
   */
  attachListener(isBack) {
    this.lastNameDOM = document.querySelector(Identity.lastNameDOMSelector);
    this.firstNameDOM = document.querySelector(Identity.firstNameDOMSelector);
    this.civilityDOM = document.querySelector(Identity.civilityDOMSelector);
    this.emailDOM = document.querySelector(Identity.emailDOMSelector);
    this.cguDOM = document.querySelector(Identity.cguDOMSelector);
    this.cguLinkDOM = document.querySelector(Identity.cguLinkDOMSelector);
    this.gdprLinkDOM = document.querySelector(Identity.gdprLinkDOMSelector);
    this.passwordDOM = document.querySelector(Identity.passwordDOMSelector);
    this.formDOM = document.querySelector(Identity.mainFormDOMSelector);

    // Identity error
    this.identityError = new IdentityError();
    // Password Visibility
    this.passwordVisibility = new PasswordVisibility();

    // Boolean State for all input
    this.isCivilityValid = isBack;
    this.isFirstNameValid = isBack;
    this.isLastNameValid = isBack;
    this.isEmailValid = Validator.isEmailValid(this.getEmailValue());
    this.isPasswordValid = false;
    this.isCGUValid = this.getCGUChecked();

    this.initValidatorValues();
    this.attachFirstNameEvent();
    this.attachLastNameEvent();
    this.attachCivilityEvent();
    this.attachCGUEvent();
    this.attachCGULinkEvent();
    this.attachGDPRLinkEvent();
    this.attachEmailEvent();
    this.attachPasswordEvent();
    this.attachSubmitEvent();
    this.passwordVisibility.attachListenerOne();
  }

  /**
   * Init Validator Values.
   */
  initValidatorValues() {
    Validator.setFirstNameValidator(this.firstNameDOM.getAttribute('pattern'));
    Validator.setLastNameValidator(this.firstNameDOM.getAttribute('pattern'));
    Validator.setEmailValidator(this.emailDOM.getAttribute('pattern'));
    window.passwordValidator.init(Identity.passwordValidatorConf);
  }

  /**
   * Attach First Name Event.
   */
  attachFirstNameEvent() {
    if (this.firstNameDOM) {
      InputEvent.handleOb1Behaviour(this.firstNameDOM);

      this.firstNameDOM.addEventListener('input', () => {
        this.controlFirstNameSyntax(false);
        InputEvent.handleOb1Behaviour(this.firstNameDOM);
      });

      this.firstNameDOM.addEventListener('blur', () => {
        this.controlFirstNameSyntax(true);
        InputEvent.handleOb1Behaviour(this.firstNameDOM);
      });

      this.firstNameDOM.addEventListener('focus', () => {
        this.controlCivilitySyntax();
      });
    }
  }

  /**
   * Attach Last Name Event.
   */
  attachLastNameEvent() {
    if (this.lastNameDOM) {
      InputEvent.handleOb1Behaviour(this.lastNameDOM);

      this.lastNameDOM.addEventListener('input', () => {
        this.controlLastNameSyntax(false);
        InputEvent.handleOb1Behaviour(this.lastNameDOM);
      });

      this.lastNameDOM.addEventListener('blur', () => {
        this.controlLastNameSyntax(true);
        InputEvent.handleOb1Behaviour(this.lastNameDOM);
      });

      this.lastNameDOM.addEventListener('focus', () => {
        this.controlCivilitySyntax();
      });
    }
  }

  /**
   * Attach Civility event.
   */
  attachCivilityEvent() {
    const inputRadios = document.querySelectorAll(Identity.civilityDOMSelector);

    for (let i = 0; i < inputRadios.length; i += 1) {
      inputRadios[i].addEventListener('change', () => {
        this.controlCivilitySyntax();
      });
    }
  }

  /**
   * Attach email event.
   */
  attachEmailEvent() {
    if (this.emailDOM) {
      InputEvent.handleOb1Behaviour(this.emailDOM);

      this.emailDOM.addEventListener('blur', () => {
        InputEvent.handleOb1Behaviour(this.emailDOM);
        this.controlEmailSyntax(true);
      });

      this.emailDOM.addEventListener('focus', () => {
        this.controlCivilitySyntax();
      });
    }
  }

  /**
   * Attach listener for the password.
   */
  attachPasswordEvent() {
    if (this.passwordDOM) {
      InputEvent.handleOb1Behaviour(this.passwordDOM);

      // listen to all input changes and exit
      this.passwordDOM.addEventListener('input', () => {
        InputEvent.handleOb1Behaviour(this.passwordDOM);
        this.passwordVisibility.clearRememberPassword();
        this.controlPasswordSyntax(false);
      });

      this.passwordDOM.addEventListener('blur', () => {
        InputEvent.handleOb1Behaviour(this.passwordDOM);
        this.controlPasswordSyntax(true);
        this.identityError.hidePasswordMessages(this.getPasswordValue() === '');
      });

      this.passwordDOM.addEventListener('focus', () => {
        this.controlCivilitySyntax();
        this.identityError.showPasswordMessages();
      });
    }
  }

  /**
   * Attach cgu event.
   */
  attachCGUEvent() {
    this.cguDOM.addEventListener('change', () => {
      this.controlCGUChecked();
    });
  }

  /**
   * Attach listener for the cgu link.
   */
  attachCGULinkEvent() {
    this.cguLinkDOM.addEventListener('click', (evt) => {
      evt.preventDefault();

      this.openCGU();
    });
  }

  /**
   * Attach listener for the gdpr link.
   */
  attachGDPRLinkEvent() {
    this.gdprLinkDOM.addEventListener('click', (evt) => {
      evt.preventDefault();

      this.openGDPR();
    });
  }

  /**
   * Attach Submit event.
   */
  attachSubmitEvent() {
    if (this.formDOM) {
      this.formDOM.addEventListener('submit', (event) => {
        event.preventDefault();

        this.submitIdentityData();
      });
    }
  }

  /**
   * Control the syntax of the first name.
   *
   * @param   {boolean} emptyCheck - If the empty property need to be checked.
   */
  controlFirstNameSyntax(emptyCheck) {
    const value = this.getFirstNameValue();

    this.isFirstNameValid = Validator.isFirstNameValid(value);
    this.identityError.handleErrorFirstName(this.isFirstNameValid, value, emptyCheck);
  }

  /**
   * Control the syntax of the last name.
   *
   * @param   {boolean} emptyCheck - If the empty property need to be checked.
   */
  controlLastNameSyntax(emptyCheck) {
    const value = this.getLastNameValue();

    this.isLastNameValid = Validator.isLastNameValid(value);
    this.identityError.handleErrorLastName(this.isLastNameValid, value, emptyCheck);
  }

  /**
   * Control the validity of the civility radios buttons.
   */
  controlCivilitySyntax() {
    const value = this.getCivilityValue();

    this.isCivilityValid = Validator.isCivilityValid(value);

    this.identityError.handleErrorCivility(this.isCivilityValid);
  }

  /**
   * Control if CGU are checked.
   */
  controlCGUChecked() {
    this.isCGUValid = this.getCGUChecked();
    this.identityError.handleErrorCGU(this.isCGUValid);
  }

  /**
   * Control the syntax of the email.
   *
   * @param   {boolean} emptyCheck - If the empty property need to be checked.
   */
  controlEmailSyntax(emptyCheck) {
    this.emailDOM.value = this.emailDOM.value.trim();
    const value = this.getEmailValue();
    this.isEmailValid = Validator.isEmailValid(value);

    this.identityError.handleErrorEmail(this.isEmailValid, value, emptyCheck);
  }

  /**
   * Control the syntax of the password.
   *
   * @param {bool} isBlurEvent - Whether the action is triggered by an onblur event.
   */
  controlPasswordSyntax(isBlurEvent) {
    if (this.isEmailSameAsPassword() && this.getEmailValue() !== '' && this.getPasswordValue() !== '') {
      // when email and password are the same, display an error instead of submitting the form
      this.identityError.setEmailSameAsPasswordError();
      this.identityError.showPasswordMessages();
      this.isPasswordValid = false;
    } else {
      const value = this.getPasswordValue();
      const passwordStatuses = window.passwordValidator.validate(value);

      this.identityError.updatePassword(passwordStatuses, isBlurEvent);
      this.isPasswordValid = !this.identityError.passwordError.isInError;
    }
  }

  /**
   * Checks whether the email and the password are the same (case-insensitive check).
   *
   * @returns {boolean} True if email and pwd are the same, false otherwise.
   */
  isEmailSameAsPassword() {
    const email = this.getEmailValue();
    const password = this.getPasswordValue();

    return email.toLowerCase() === password.toLowerCase();
  }

  /**
   * Get civility value.
   *
   * @returns {string} Civility.
   */
  getCivilityValue() {
    this.civilityDOM = document.querySelector(`${Identity.civilityDOMSelector}:checked`);

    return this.civilityDOM ? this.civilityDOM.value : null;
  }

  /**
   * Get lastName value.
   *
   * @returns {string} LastNameDomValue.
   */
  getLastNameValue() {
    return this.lastNameDOM.value;
  }

  /**
   * Get firstName value.
   *
   * @returns {string} FirstName.
   */
  getFirstNameValue() {
    return this.firstNameDOM.value;
  }

  /**
   * Getter email value.
   *
   * @returns {string} Email value.
   */
  getEmailValue() {
    return this.emailDOM.value;
  }

  /**
   * Getter password value.
   *
   * @returns {string} Password value.
   */
  getPasswordValue() {
    return this.passwordDOM.value;
  }

  /**
   * Getter checked attribute of cgu checkbox.
   *
   * @returns {boolean} Whether the cgu checkbox is checked or not.
   */
  getCGUChecked() {
    return this.cguDOM.checked;
  }

  /**
   * Init value validator.
   */
  initEmailValidator() {
    Validator.setEmailValidator(this.emailDOM.getAttribute('pattern'));
  }

  /**
   * Is the form is valid.
   *
   * @returns {boolean} If the formulaire is valid or not.
   */
  isFormValid() {
    return this.isCivilityValid && this.isFirstNameValid && this.isLastNameValid && this.isEmailValid && this.isPasswordValid && this.isCGUValid;
  }

  /**
   * Submit Identity Data.
   */
  submitIdentityData() {
    // Check all value before submit and upgrade state of boolean value
    this.controlFirstNameSyntax(true);
    this.controlLastNameSyntax(true);
    this.controlCivilitySyntax();
    this.controlEmailSyntax(true);
    this.controlPasswordSyntax(true);
    this.controlCGUChecked();
    this.identityError.showPasswordMessages();
    this.identityError.hidePasswordMessages(this.getPasswordValue() === '');
    if (this.isFormValid()) {
      const opt = {
        url: ApiService.apiSchema.identity.url,
        method: ApiService.apiSchema.identity.method,
        data: {
          firstName: this.getFirstNameValue(),
          lastName: this.getLastNameValue(),
          civility: this.getCivilityValue(),
          login: this.getEmailValue(),
          password: this.getPasswordValue(),
          cgu: this.getCGUChecked(),
        },
      };

      // Store the password value into the dom
      Identity.passwordValue = this.getPasswordValue();

      return Component.makeRequest(opt)
        .then((requestPending) => {
          if (requestPending !== true) {
            SessionService.restartSessionTimer();
          }
        })
        .catch(() => undefined);
    }

    this.handleScrollError();
  }

  /**
   * Handle Scroll to first error.
   */
  handleScrollError() {
    const elementToError = {};

    elementToError[Identity.civilityDOMSelector] = this.isCivilityValid;
    elementToError[Identity.firstNameDOMSelector] = this.isFirstNameValid;
    elementToError[Identity.lastNameDOMSelector] = this.isLastNameValid;
    elementToError[Identity.emailDOMSelector] = this.isEmailValid;
    elementToError[Identity.passwordDOMSelector] = this.isPasswordValid;

    const firstErrorDomSelector = Object.keys(elementToError).find((key) => elementToError[key] === false);

    const firstErrorDom = document.querySelector(firstErrorDomSelector);
    if (firstErrorDom) {
      firstErrorDom.focus();
    }
  }

  /**
   * Call the api with CGU schema config.
   */
  openCGU() {
    return this.openLegalPage(ApiService.apiSchema.cgu.url, ApiService.apiSchema.cgu.method);
  }

  /**
   * Call the api with GDPR schema config.
   */
  openGDPR() {
    return this.openLegalPage(ApiService.apiSchema.gdpr.url, ApiService.apiSchema.gdpr.method);
  }

  /**
   * Call the api with url and method.
   *
   * @param {string} url - The url to call the api.
   * @param {string} method - The method to call the api.
   */
  openLegalPage(url, method) {
    const data = {
      cgu: this.getCGUChecked(),
    };

    // Inject the login
    const login = this.getEmailValue();
    if (login && this.isEmailValid) {
      data.login = login;
    }

    // Inject the firstName
    const firstName = this.getFirstNameValue();
    if (firstName && this.isFirstNameValid) {
      data.firstName = firstName;
    }

    // Inject the lastName
    const lastName = this.getLastNameValue();
    if (lastName && this.isLastNameValid) {
      data.lastName = lastName;
    }

    const civility = this.getCivilityValue();
    if (civility) {
      data.civility = civility;
    }

    const opt = {
      url,
      method,
      data,
    };

    return Component.makeRequest(opt)
      .then((requestPending) => {
        if (requestPending !== true) {
          SessionService.restartSessionTimer();
        }
      });
  }
}
